import { useEffect, useMemo, useState } from 'react'
import {Building2, ChevronDown, ChevronRight, MapPin, Pencil, Plus, Search, Trash2} from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '@/lib/lumi'
import { useAppAuth } from '@/contexts/AuthContext'
import SubLocalModal from '@/components/sublocais/SubLocalModal'
import { formatDateBR } from '@/utils/masks'

const tipoLabels: Record<string, string> = {
  ESCOLA: 'Escola',
  UNIDADE_SAUDE: 'Unidade de Saúde',
  UNIDADE_ASSISTENCIA_SOCIAL: 'Unidade de Assistência Social',
  OUTRO: 'Outro',
}

function SubLocais() {
  const { user } = useAppAuth()
  const isAdmin = user?.role === 'ADMIN'
  const isModerador = user?.role === 'MODERADOR'
  const isGestor = user?.role === 'GESTOR'

  const [subLocais, setSubLocais] = useState<any[]>([])
  const [cidades, setCidades] = useState<any[]>([])
  const [secretarias, setSecretarias] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [busca, setBusca] = useState('')
  const [cidadeFiltro, setCidadeFiltro] = useState<string>(isAdmin ? 'TODAS' : user?.cidadeId || '')
  const [secretariaFiltro, setSecretariaFiltro] = useState<string>('TODAS')
  const [modalAberto, setModalAberto] = useState(false)
  const [editando, setEditando] = useState<any>(null)
  const [excluindo, setExcluindo] = useState<any>(null)
  const [secretariasColapsadas, setSecretariasColapsadas] = useState<Record<string, boolean>>({})

  const fetchDados = async () => {
    setLoading(true)
    try {
      const [resSub, resCidades, resSecretarias] = await Promise.all([
        (lumi.entities as any).sublocais.list({ sort: { nome: 1 }, limit: 500 }),
        (lumi.entities as any).cidades.list({ sort: { nome: 1 }, limit: 200 }),
        (lumi.entities as any).secretarias.list({ sort: { nome: 1 }, limit: 200 }),
      ])
      setSubLocais(resSub?.list || [])
      setCidades(resCidades?.list || [])
      setSecretarias(resSecretarias?.list || [])
    }
    catch {
      toast.error('Erro ao carregar sub locais.')
    }
    finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDados()
  }, [])

  // ADMIN vê tudo. MODERADOR vê tudo da sua cidade. GESTOR vê os sub locais da sua secretaria (dentro da sua cidade).
  const subLocaisVisiveis = subLocais.filter((s) => {
    if (isAdmin) return true
    if (isModerador) return s.cidadeId === user?.cidadeId
    if (isGestor) return s.cidadeId === user?.cidadeId && s.secretariaId === user?.secretariaId
    return false
  })

  const cidadesVisiveis = isAdmin ? cidades : cidades.filter(c => c._id === user?.cidadeId)
  const secretariasVisiveis = isAdmin || isModerador
    ? secretarias.filter(s => cidadeFiltro === 'TODAS' || s.cidadeId === cidadeFiltro)
    : secretarias.filter(s => s._id === user?.secretariaId)

  const subLocaisFiltrados = subLocaisVisiveis.filter((s) => {
    const passaCidade = cidadeFiltro === 'TODAS' || s.cidadeId === cidadeFiltro
    const passaSecretaria = secretariaFiltro === 'TODAS' || s.secretariaId === secretariaFiltro
    const passaBusca = s.nome?.toLowerCase().includes(busca.toLowerCase())
    return passaCidade && passaSecretaria && passaBusca
  })

  const nomeCidade = (id: string) => cidades.find(c => c._id === id)?.nome || '-'

  const podeGerir = isAdmin || isModerador

  // Agrupa os sub locais filtrados por secretaria, exibindo apenas grupos com resultados.
  const gruposPorSecretaria = useMemo(() => {
    const mapa = new Map<string, any[]>()
    subLocaisFiltrados.forEach((s) => {
      const lista = mapa.get(s.secretariaId) || []
      lista.push(s)
      mapa.set(s.secretariaId, lista)
    })
    const secretariasComDados = secretarias
      .filter(sec => mapa.has(sec._id))
      .sort((a, b) => a.nome.localeCompare(b.nome))
    return secretariasComDados.map(sec => ({
      secretaria: sec,
      itens: mapa.get(sec._id) || [],
    }))
  }, [subLocaisFiltrados, secretarias])

  const toggleSecretaria = (id: string) => {
    setSecretariasColapsadas(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const handleExcluir = async () => {
    if (!excluindo) return
    try {
      await (lumi.entities as any).sublocais.delete(excluindo._id)
      toast.success('Sub local excluído com sucesso.')
      setExcluindo(null)
      fetchDados()
    }
    catch {
      toast.error('Erro ao excluir sub local.')
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-[20px] font-semibold text-[#12141A]">Sub Locais</h1>
          <p className="text-[13.5px] text-[#6B7280] mt-0.5">
            Escolas, unidades de saúde e unidades de assistência social, agrupadas por secretaria.
          </p>
        </div>
        {podeGerir && (
          <button
            onClick={() => { setEditando(null); setModalAberto(true) }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1E4FBF] hover:bg-[#1A44A6] text-white text-[13.5px] font-medium"
          >
            <Plus className="w-4 h-4" strokeWidth={1.75} />
            Novo Sub Local
          </button>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-[320px]">
          <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
          <input
            value={busca}
            onChange={e => setBusca(e.target.value)}
            placeholder="Pesquisar sub local..."
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
          />
        </div>

        {(isAdmin || isModerador) && (
          <select
            value={cidadeFiltro}
            onChange={(e) => { setCidadeFiltro(e.target.value); setSecretariaFiltro('TODAS') }}
            className="px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
          >
            <option value="TODAS">Todas as cidades</option>
            {cidadesVisiveis.map(c => <option key={c._id} value={c._id}>{c.nome}</option>)}
          </select>
        )}

        <select
          value={secretariaFiltro}
          onChange={e => setSecretariaFiltro(e.target.value)}
          className="px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
        >
          <option value="TODAS">Todas as secretarias</option>
          {secretariasVisiveis.map(s => <option key={s._id} value={s._id}>{s.nome}</option>)}
        </select>
      </div>

      <div className="flex flex-col gap-4">
        {loading && (
          <div className="bg-white rounded-2xl border border-[#E7E9EC] px-5 py-10 text-center text-[#9CA3AF] text-[13.5px]">
            A carregar...
          </div>
        )}

        {!loading && gruposPorSecretaria.length === 0 && (
          <div className="bg-white rounded-2xl border border-[#E7E9EC] px-5 py-10 text-center text-[#9CA3AF] text-[13.5px]">
            Nenhum sub local encontrado.
          </div>
        )}

        {!loading && gruposPorSecretaria.map(({ secretaria, itens }) => {
          const colapsada = !!secretariasColapsadas[secretaria._id]
          return (
            <div key={secretaria._id} className="bg-white rounded-2xl border border-[#E7E9EC] overflow-hidden">
              <button
                onClick={() => toggleSecretaria(secretaria._id)}
                className="w-full flex items-center justify-between px-5 py-3.5 bg-[#F9FAFB] hover:bg-[#F1F2F4] transition-colors"
              >
                <div className="flex items-center gap-2.5">
                  {colapsada ? <ChevronRight className="w-4 h-4 text-[#6B7280]" strokeWidth={1.75} /> : <ChevronDown className="w-4 h-4 text-[#6B7280]" strokeWidth={1.75} />}
                  <div className="w-8 h-8 rounded-lg bg-[#EEF3FE] flex items-center justify-center shrink-0">
                    <Building2 className="w-4 h-4 text-[#1E4FBF]" strokeWidth={1.75} />
                  </div>
                  <div className="text-left">
                    <span className="font-semibold text-[13.5px] text-[#12141A] block">{secretaria.nome}</span>
                    {(isAdmin || isModerador) && (
                      <span className="text-[11.5px] text-[#9CA3AF]">{nomeCidade(secretaria.cidadeId)}</span>
                    )}
                  </div>
                </div>
                <span className="text-[12px] font-medium text-[#6B7280] bg-[#F1F2F4] px-2.5 py-1 rounded-full">
                  {itens.length}
                  {' '}
                  {itens.length === 1 ? 'local' : 'locais'}
                </span>
              </button>

              {!colapsada && (
                <table className="w-full text-[13.5px]">
                  <thead>
                    <tr className="border-b border-[#E7E9EC] text-left">
                      <th className="px-5 py-2.5 font-medium text-[#9CA3AF] text-[12px]">Sub Local</th>
                      <th className="px-5 py-2.5 font-medium text-[#9CA3AF] text-[12px]">Tipo</th>
                      <th className="px-5 py-2.5 font-medium text-[#9CA3AF] text-[12px]">Criado em</th>
                      {podeGerir && <th className="px-5 py-2.5 font-medium text-[#9CA3AF] text-[12px] text-right">Ações</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {itens.map(s => (
                      <tr key={s._id} className="border-b border-[#F1F2F4] last:border-0 hover:bg-[#F9FAFB]">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-lg bg-[#F1F2F4] flex items-center justify-center shrink-0">
                              <MapPin className="w-3.5 h-3.5 text-[#6B7280]" strokeWidth={1.75} />
                            </div>
                            <div>
                              <span className="font-medium text-[#12141A] block">{s.nome}</span>
                              {s.endereco && <span className="text-[12px] text-[#9CA3AF]">{s.endereco}</span>}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-[#4B5563]">{tipoLabels[s.tipo] || s.tipo || '-'}</td>
                        <td className="px-5 py-3 text-[#4B5563]">{formatDateBR(s.createdAt)}</td>
                        {podeGerir && (
                          <td className="px-5 py-3 text-right whitespace-nowrap">
                            <button
                              onClick={() => { setEditando(s); setModalAberto(true) }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-medium text-[#374151] border border-[#D1D5DB] hover:bg-[#F5F6F8] mr-2"
                            >
                              <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                              Editar
                            </button>
                            {isAdmin && (
                              <button
                                onClick={() => setExcluindo(s)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-medium text-[#B91C1C] border border-[#FCA5A5] hover:bg-[#FEF2F2]"
                              >
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                                Excluir
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )
        })}
      </div>

      {modalAberto && (
        <SubLocalModal
          subLocal={editando}
          cidades={cidadesVisiveis}
          secretarias={secretarias}
          cidadeIdFixa={isAdmin ? undefined : user?.cidadeId}
          onClose={() => setModalAberto(false)}
          onSaved={fetchDados}
        />
      )}

      {excluindo && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-[420px] p-6">
            <h3 className="text-[15px] font-semibold text-[#12141A] mb-2">Excluir sub local</h3>
            <p className="text-[13.5px] text-[#6B7280] mb-5">
              Tem certeza que deseja excluir <span className="font-medium text-[#12141A]">{excluindo.nome}</span>? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setExcluindo(null)} className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium border border-[#D1D5DB] hover:bg-[#F5F6F8]">
                Cancelar
              </button>
              <button onClick={handleExcluir} className="px-4 py-2.5 rounded-lg text-[13.5px] font-medium bg-[#B91C1C] hover:bg-[#991B1B] text-white">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SubLocais
